import { COST_KIND_LABEL, PICKER_MSG, RATE_BASE_LABEL, TAX_CATEGORY_LABEL, type RateBase, type CostKind, type TaxCategory } from "@genbaryoku/contract";
import { useEffect, useRef } from "react";
import { fq, mdSlash, yen } from "../lib/num.js";
import { Icon } from "./Shell.jsx";
import { Help, Switch } from "./ui.jsx";
import { t } from "../labels.js";

/**
 * 候補の引き出し（試作 v3.10.1、縦串3b → #215 で Figma「候補の引き出し（10/19）」193:54 の形に）。名称を打つと画面の右から紙に重ねて出る（#214）。
 * 出どころは4つ：この見積（同じ見積の別の行）／過去の見積（回数・前回の見積日つき）／品目（品目マスタ。T2：選ぶと税区分も写る）／率の名前（会社設定）。
 * ↑↓で選び Enter で行に写す（カーソルは名称の欄のまま）。Esc で閉じる。「続けて選ぶ」（頭のスイッチ）で選ぶたびに下に行が足される。
 * 幅 560px・タブは同じ幅で 44px・候補の行は2行 64px（上＝名称 17px と しるし、下＝規格・使用回数・前回・仕入れ を薄墨 14px で1行）。
 * 引き出しには入力欄を置かない（行の編集は行の中。docs/10 §5.5）・操作の説明の文を並べない（「？」へ。docs/10 §5.1.1）
 */
export type Cand =
  | { src: "here" | "past"; name: string; spec: string; unit: string; price: number | null; cost: number | null; count?: number; last?: string; taxCategory?: TaxCategory | undefined }
  | { src: "item"; id: string; name: string; unit: string; taxCategory: TaxCategory }
  | { src: "rate"; name: string; rate: number; base: RateBase; kind: CostKind | null };

export type PickTab = "all" | "here" | "past" | "item" | "rate";
export const PICK_TABS: Array<[PickTab, string]> = (["all", "here", "past", "item", "rate"] as const).map((k) => [k, PICKER_MSG.tabs[k]]);

/** 率の基礎の短い名前（「選んだ小計に対して」→「選んだ小計」） */
const baseShort = (b: RateBase): string => RATE_BASE_LABEL[b].split("（")[0]!.replace(/に対して$/, "");
const fill = (tpl: string, k: string, v: string) => tpl.replace(`{${k}}`, v);

/** 候補の行の下段（規格・使用回数・前回・仕入れ…）。規格と数字のあいだは全角の空き、数字どうしは「・」 */
function subOf(c: Cand): { spec: string; meta: string[] } {
  if (c.src === "item") return { spec: "", meta: [c.unit ? fill(PICKER_MSG.unit, "u", c.unit) : "", fill(PICKER_MSG.tax, "t", TAX_CATEGORY_LABEL[c.taxCategory])].filter(Boolean) };
  if (c.src === "rate") return { spec: "", meta: [fill(PICKER_MSG.base, "b", baseShort(c.base)), c.kind ? COST_KIND_LABEL[c.kind] : ""].filter(Boolean) };
  return {
    spec: c.spec,
    meta: [
      c.src === "past" && c.count != null ? fill(PICKER_MSG.times, "n", String(c.count)) : "",
      c.src === "past" && c.last ? fill(PICKER_MSG.last, "d", mdSlash(c.last)) : "",
      c.cost != null ? fill(PICKER_MSG.cost, "v", yen(c.cost)) : "",
    ].filter(Boolean),
  };
}

export function Picker({
  open, q, tab, sel, multi, groups, showItems = false, tabs, hideMulti = false, list, loading, onQ, onTab, onMove, onPick, onMulti, onClose,
}: {
  open: boolean; q: string; tab: PickTab; sel: number; multi: boolean;
  /** タブごとの候補（すべて＝ここから抜粋） */
  groups: { here: Cand[]; past: Cand[]; item: Cand[]; rate: Cand[] };
  /** 「品目」のタブを出すか（使う機能「品目マスタ」。T2） */
  showItems?: boolean;
  /** 出すタブ（省けば全部。受け取った書類の確認の引き出しは すべて・前の見積・品目 だけ＝R2） */
  tabs?: readonly PickTab[];
  /** 「続けて選ぶ」を出さない（1行の名称を選び直すだけの所） */
  hideMulti?: boolean;
  /** いま並んでいる候補（↑↓ の対象） */
  list: Cand[];
  loading: boolean;
  onQ: (q: string) => void; onTab: (t: PickTab) => void; onMove: (d: 1 | -1) => void; onPick: (i: number) => void; onMulti: (on: boolean) => void; onClose: () => void;
}) {
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // 開いている印（受け取った書類の引き出しが Esc を譲るのに見る）。紙は寄せない＝引き出しは紙に重ねる（#214）
    document.body.classList.toggle("pick-open", open);
    return () => document.body.classList.remove("pick-open");
  }, [open]);
  useEffect(() => {
    listRef.current?.querySelector(".pk.hi")?.scrollIntoView({ block: "nearest" });
  }, [sel, list]);
  if (!open) return null;

  let idx = 0;
  const row = (c: Cand) => {
    const i = idx++;
    const { spec, meta } = subOf(c);
    const chip = c.src === "item" ? PICKER_MSG.chipItem : c.src === "rate" ? PICKER_MSG.chipRate : c.src === "here" ? PICKER_MSG.chipHere : null;
    return (
      <button type="button" key={`${c.src}:${c.name}:${"spec" in c ? c.spec : ""}`} className={"pk " + (c.src === "rate" ? "rt " : "") + (c.src === "here" ? "here " : "") + (c.src === "item" ? "item " : "") + (i === sel ? "hi" : "")} onMouseDown={(e) => e.preventDefault()} onClick={() => onPick(i)}>
        <span className="n">
          <span className="nt">{c.name}</span>
          {chip ? <span className="pchip">{chip}</span> : null}
        </span>
        <span className="m">
          {c.src === "rate" ? (
            <>
              {fq(Math.round(c.rate * 10000) / 100)}<small> %</small>
            </>
          ) : c.src === "item" ? null : c.price != null ? (
            <>
              {yen(c.price)}<small> 円／{c.unit || "式"}</small>
            </>
          ) : (
            <small>{PICKER_MSG.noPrice}</small>
          )}
        </span>
        <span className="s">
          {spec}
          {spec && meta.length ? "\u3000" : ""}
          {meta.join(" ・ ")}
        </span>
      </button>
    );
  };
  // 「すべて」は出どころを全部並べる（抜粋にしない。射場さん 9/13）。#215：出どころの見出しは置かず、品目・率・この見積は名称の右のしるしで分ける
  const shown: Cand[] = tab === "all" ? [...groups.here, ...groups.past, ...groups.item, ...groups.rate] : groups[tab];
  const body = shown.map(row);
  const sortLine = tab === "all" || tab === "past";

  return (
    <aside className="picker open" role="dialog" aria-label={PICKER_MSG.title}>
      <div className="ph">
        <b>{PICKER_MSG.title}</b>
        {/* 選び方は「？」へ（#92・#215）。押しても名称の欄から focus を移さない（↓↑ と Enter が効いたまま） */}
        <span onMouseDown={(e) => e.preventDefault()}>
          <Help testId="help-picker" label={PICKER_MSG.helpLabel} text={t(PICKER_MSG.help)} />
        </span>
        <span className="sp" />
        {hideMulti ? null : (
          <span className="pmulti" onMouseDown={(e) => e.preventDefault()}>
            <Switch checked={multi} onChange={onMulti} label={PICKER_MSG.multi} testId="picker-multi" />
          </span>
        )}
        <button type="button" className="x" onClick={onClose} aria-label={PICKER_MSG.close} title={PICKER_MSG.close}>
          <Icon k="x" />
        </button>
      </div>
      <div className="ps">
        <span className="psi" aria-hidden="true">
          <Icon k="search" />
        </span>
        <input
          className="inp"
          id="pk_q"
          value={q}
          placeholder={t(PICKER_MSG.search)}
          aria-label={PICKER_MSG.searchLabel}
          onChange={(e) => onQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.nativeEvent.isComposing || e.keyCode === 229) return;
            if (e.key === "ArrowDown" || e.key === "ArrowUp") {
              e.preventDefault();
              onMove(e.key === "ArrowDown" ? 1 : -1);
            } else if (e.key === "Enter") {
              e.preventDefault();
              if (list.length) onPick(sel);
            } else if (e.key === "Escape") {
              e.preventDefault();
              onClose();
            }
          }}
        />
        {/* 打ち込み中は件数だけ（#215。「「○○」で絞り込み」はやめた） */}
        {q.trim() ? <span className="pcount num" data-testid="picker-count">{PICKER_MSG.count.replace("{n}", String(list.length))}</span> : null}
      </div>
      <div className="ptabs">
        {PICK_TABS.filter(([k]) => (k !== "item" || showItems) && (!tabs || tabs.includes(k))).map(([k, label]) => (
          <button type="button" key={k} className={tab === k ? "is-on" : ""} aria-pressed={tab === k} onMouseDown={(e) => e.preventDefault()} onClick={() => onTab(k)}>
            {label}
          </button>
        ))}
      </div>
      {sortLine ? <div className="psort">{PICKER_MSG.sort}</div> : null}
      <div className="pl" ref={listRef}>
        {body.length ? body : <div className="pempty">{loading ? PICKER_MSG.loading : q ? t(PICKER_MSG.none) : PICKER_MSG.empty}</div>}
      </div>
    </aside>
  );
}
